import { Component, computed, effect, input, output, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FilterHeaderComponent } from '@vectronic/ui';
export type MinMaxOption = { min?: number; max?: number; label?: string };
export type MinMaxValue = Record<string, { min?: number; max?: number } | undefined>;
@Component({
  selector: 'table-datatable-filter-min-max',
  templateUrl: './datatable-filter-min-max.component.html',
  styleUrls: ['./datatable-filter-min-max.component.scss'],
  imports: [MatFormFieldModule, MatInputModule, FilterHeaderComponent],
})
export class DatatableFilterMinMaxComponent {
  title = input.required<string>();
  value = input<MinMaxValue | undefined>();
  expanded = input.required<boolean>();
  options = input<Record<string, MinMaxOption>>();

  valueChange = output<MinMaxValue | undefined>();
  toggled = output<void>();

  localValue = signal<MinMaxValue | undefined>(undefined);

  private syncLocalValueWithInputEffect = effect(() => {
    this.localValue.set(this.value());
  });

  getKeys = computed(() => {
    const opts = this.options();
    return opts ? Object.keys(opts) : [];
  });

  getValue(key: string, type: 'min' | 'max'): number | undefined {
    return this.localValue()?.[key]?.[type];
  }

  onFilterChange(key: string, type: 'min' | 'max', event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    const numberValue = inputValue === '' ? undefined : Number(inputValue);

    const currentFilterValue = this.localValue() || {};
    const currentKeyValue = currentFilterValue[key] || {};

    const newValue = {
      ...currentFilterValue,
      [key]: {
        ...currentKeyValue,
        [type]: numberValue,
      },
    };

    if (newValue[key].min === undefined && newValue[key].max === undefined) {
      delete newValue[key];
    }

    this.localValue.set(newValue);

    // Validate before emitting
    const isInvalid =
      this.isRangeInvalid(key) ||
      this.isOptionInvalid(key, 'min') !== null ||
      this.isOptionInvalid(key, 'max') !== null;

    if (!isInvalid) {
      if (Object.keys(newValue).length === 0) {
        this.valueChange.emit(undefined);
      } else {
        this.valueChange.emit(newValue);
      }
    }
  }
  isRangeInvalid(key: string): boolean {
    const min = this.getValue(key, 'min');
    const max = this.getValue(key, 'max');
    return min !== undefined && max !== undefined && min > max;
  }

  isOptionInvalid(key: string, type: 'min' | 'max'): string | null {
    const value = this.getValue(key, type);
    const option = this.options()?.[key];

    if (value !== undefined && option) {
      if (option.min !== undefined && value < option.min) {
        return `Value < ${option.min}`;
      }
      if (option.max !== undefined && value > option.max) {
        return `Value > ${option.max}`;
      }
    }
    return null;
  }
}
